import { Check, Pin } from "lucide-react"
import { Button } from "@/components/ui/Button"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/DropdownMenu"

export type FlagChoice = "true" | "false" | "auto"

interface Props {
  value: boolean
  manual: boolean
  canManage: boolean
  onSelect: (next: FlagChoice) => void
}

// One selling/gifting cell. Value changes go through a menu (not a bare
// checkbox) so a misclick can't silently pin a manual value.
export default function FlagCell({ value, manual, canManage, onSelect }: Props) {
  const display = (
    <span
      className={`inline-flex items-center gap-1 ${value ? "text-foreground" : "text-muted-foreground"}`}
    >
      {value ? "Yes" : "No"}
      {manual && <Pin className="h-3 w-3 text-muted-foreground" />}
    </span>
  )

  if (!canManage) return display

  const options: { choice: FlagChoice; label: string; active: boolean }[] = [
    { choice: "true", label: "Yes — set manually", active: manual && value },
    { choice: "false", label: "No — set manually", active: manual && !value },
    { choice: "auto", label: "Auto — follow sales data", active: !manual },
  ]

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 px-2 font-normal">
          {display}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {options.map((o) => (
          <DropdownMenuItem
            key={o.choice}
            onClick={() => !o.active && onSelect(o.choice)}
            className="justify-between gap-4"
          >
            {o.label}
            {o.active && <Check className="h-3.5 w-3.5" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
